import { useEffect, useMemo, useState } from "react";
import { Baby, Heart, UserPlus, HelpCircle, CheckCircle2, AlertTriangle, FileText, ListOrdered, Loader2, Mail, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

const REASONS = [
  { id: "newborn", label: "New baby", icon: Baby, desc: "Born after the petition was filed" },
  { id: "new_marriage", label: "New spouse", icon: Heart, desc: "Married after the petition was approved" },
  { id: "left_off", label: "Left off the petition", icon: UserPlus, desc: "Existed but isn't on the NVC/CEAC record" },
  { id: "other", label: "Other", icon: HelpCircle, desc: "Another situation" },
];

function RelationshipPicker({ value, onChange }) {
  return (
    <>
      <label className="field-label">Who are you adding?</label>
      <div className="flex gap-2 mb-4">
        {[{ id: "child", label: "Child" }, { id: "spouse", label: "Spouse" }].map((r) => (
          <button key={r.id} type="button" data-testid={`member-rel-${r.id}`}
            onClick={() => onChange(r.id)}
            className="px-4 py-1.5 rounded-full text-sm border transition-colors"
            style={{
              background: value === r.id ? "var(--ai-bg)" : "#fff",
              borderColor: value === r.id ? "var(--ai-border)" : "var(--border-stone)",
            }}>
            {r.label}
          </button>
        ))}
      </div>
    </>
  );
}

function ReasonPicker({ value, onChange }) {
  return (
    <>
      <label className="field-label">Why are they being added?</label>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {REASONS.map((r) => {
          const Icon = r.icon;
          const active = value === r.id;
          return (
            <button key={r.id} type="button" data-testid={`member-reason-${r.id}`}
              onClick={() => onChange(r.id)}
              className="text-left p-3 rounded-lg border transition-colors"
              style={{
                background: active ? "var(--ai-bg)" : "#fff",
                borderColor: active ? "var(--ai-border)" : "var(--border-stone)",
              }}>
              <Icon className="w-4 h-4 mb-1" style={{ color: active ? "var(--action)" : "var(--text-secondary)" }} />
              <div className="font-semibold text-xs" style={{ color: "var(--text-primary)" }}>{r.label}</div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{r.desc}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function MemberDetailFields({ form, set, familyCategories, employmentCategories }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="field-label">First name</label>
          <input className="field-input" required value={form.first_name} onChange={set("first_name")} data-testid="member-first-name" />
        </div>
        <div>
          <label className="field-label">Last name</label>
          <input className="field-input" value={form.last_name} onChange={set("last_name")} data-testid="member-last-name" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="field-label">Date of birth</label>
          <input className="field-input" placeholder="YYYY-MM-DD" value={form.dob} onChange={set("dob")} data-testid="member-dob" />
        </div>
        <div>
          <label className="field-label">Your visa category</label>
          <select className="field-input" value={form.visa_category} onChange={set("visa_category")} data-testid="member-visa-category">
            <option value="">Not sure</option>
            <optgroup label="Family-based (I-130)">
              {familyCategories.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </optgroup>
            <optgroup label="Employment-based (I-140)">
              {employmentCategories.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
      </div>
    </>
  );
}

/* ---------------- Add Family Member modal (form → guidance result) ---------------- */
export default function AddFamilyMemberModal({ caseId, caseData, onClose, onAdded }) {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", dob: "",
    relationship: "child", reason: "newborn",
    visa_category: caseData?.data?.nvc_visa_category || "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const familyCategories = useMemo(() => categories.filter((c) => c.group === "family"), [categories]);
  const employmentCategories = useMemo(() => categories.filter((c) => c.group === "employment"), [categories]);

  useEffect(() => {
    api.get("/family-members/visa-categories")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/family-members`, form);
      setResult(data);
      onAdded?.(data.person_ref);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't add this family member. Try again in a moment."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalFrame onClose={onClose} testId="add-member-modal">
      {!result ? (
        <form onSubmit={submit}>
          <h3 className="font-display text-2xl font-bold mb-1">Add a family member</h3>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
            New baby, new spouse, or someone missing from your NVC record — we'll check eligibility
            and tell you exactly which documents they need.
          </p>

          <RelationshipPicker value={form.relationship}
            onChange={(id) => setForm((f) => ({ ...f, relationship: id }))} />

          <ReasonPicker value={form.reason}
            onChange={(id) => setForm((f) => ({ ...f, reason: id }))} />

          <MemberDetailFields form={form} set={set}
            familyCategories={familyCategories} employmentCategories={employmentCategories} />

          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-ghost text-sm" onClick={onClose} data-testid="member-cancel-btn">Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || !form.first_name.trim()} data-testid="member-submit-btn">
              {saving ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Adding…</span> : "Add & check eligibility"}
            </button>
          </div>
        </form>
      ) : (
        <GuidancePanel
          memberName={`${result.member.first_name} ${result.member.last_name}`.trim()}
          guidance={result.guidance}
          caseId={caseId}
          memberUid={result.member.uid}
          onClose={onClose}
        />
      )}
    </ModalFrame>
  );
}

/* ---------------- Guidance viewer (for existing after-acquired members) ---------------- */
export function FamilyMemberGuidanceModal({ caseId, uid, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/cases/${caseId}/family-members/${uid}/guidance`)
      .then(({ data }) => setData(data))
      .catch((err) => {
        toast.error(friendlyError(err, "Couldn't load guidance."));
        onClose();
      });
  }, [caseId, uid, onClose]);

  return (
    <ModalFrame onClose={onClose} testId="member-guidance-modal">
      {!data ? (
        <div className="flex items-center gap-2 text-sm py-6" style={{ color: "var(--text-secondary)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading guidance…
        </div>
      ) : (
        <GuidancePanel
          memberName={`${data.member.first_name} ${data.member.last_name}`.trim()}
          guidance={data.guidance}
          caseId={caseId}
          memberUid={uid}
          onClose={onClose}
        />
      )}
    </ModalFrame>
  );
}

/* ---------------- Shared pieces ---------------- */
function ModalFrame({ children, onClose, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(26,46,36,0.45)" }} onClick={onClose}>
      <div className="card-surface p-6 sm:p-8 max-w-xl w-full anim-in my-6"
        onClick={(e) => e.stopPropagation()} data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

function GuidancePanel({ memberName, guidance, caseId, memberUid, onClose }) {
  const el = guidance.eligibility;
  const eligible = el.status === "eligible_derivative";
  const unknown = el.status === "unknown_category";
  const banner = eligible
    ? { bg: "#ECFDF5", border: "#A7F3D0", color: "#065F46", Icon: CheckCircle2 }
    : unknown
      ? { bg: "var(--ai-bg)", border: "var(--ai-border)", color: "var(--text-primary)", Icon: HelpCircle }
      : { bg: "#FFFBEB", border: "#FDE68A", color: "#92400E", Icon: AlertTriangle };
  const BannerIcon = banner.Icon;

  return (
    <div data-testid="member-guidance-panel">
      <h3 className="font-display text-xl font-bold mb-3">What {memberName || "this person"} needs</h3>

      <div className="rounded-lg border p-4 mb-4" style={{ background: banner.bg, borderColor: banner.border }} data-testid="member-eligibility-banner">
        <div className="flex items-start gap-2">
          <BannerIcon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: banner.color }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: banner.color }}>{el.headline}</div>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{el.explanation}</p>
          </div>
        </div>
        {el.warnings?.length > 0 && (
          <ul className="mt-2 pl-6 list-disc space-y-1 text-xs" style={{ color: "var(--text-secondary)" }}>
            {el.warnings.map((w) => <li key={w}>{w}</li>)}
          </ul>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center gap-1.5 font-semibold text-sm mb-2" style={{ color: "var(--text-primary)" }}>
          <FileText className="w-4 h-4" style={{ color: "var(--action)" }} /> Documents to collect & upload
        </div>
        <ul className="space-y-1.5" data-testid="member-doc-checklist">
          {guidance.documents.map((d) => (
            <li key={d.id} className="text-xs flex items-start gap-2" data-testid={`member-doc-${d.id}`}>
              <span className="w-3.5 h-3.5 mt-0.5 rounded-full border shrink-0" style={{ borderColor: "var(--border-stone)" }} />
              <span>
                <span style={{ color: "var(--text-primary)" }}>{d.label}</span>
                {d.optional && <span className="ml-1" style={{ color: "var(--text-secondary)" }}>(if applicable)</span>}
                {d.why && <span className="block text-[10px]" style={{ color: "var(--text-secondary)" }}>{d.why}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-5">
        <div className="flex items-center gap-1.5 font-semibold text-sm mb-2" style={{ color: "var(--text-primary)" }}>
          <ListOrdered className="w-4 h-4" style={{ color: "var(--action)" }} /> {eligible ? "How to get them onto your NVC case" : "Next steps"}
        </div>
        <ol className="space-y-1.5 text-xs" data-testid="member-nvc-steps">
          {guidance.nvc_steps.map((s) => (
            <li key={s.step} className="flex gap-2">
              <span className="font-bold shrink-0" style={{ color: "var(--action)" }}>{s.step}.</span>
              <span style={{ color: "var(--text-secondary)" }}>{s.text}</span>
            </li>
          ))}
        </ol>
      </div>

      {eligible && caseId && memberUid && (
        <NvcLetterSection caseId={caseId} memberUid={memberUid} />
      )}

      <div className="flex justify-end">
        <button type="button" className="btn-primary" onClick={onClose} data-testid="member-guidance-done-btn">
          Got it — upload their documents
        </button>
      </div>
    </div>
  );
}

/* ---------------- One-click NVC letter draft ---------------- */
function NvcLetterSection({ caseId, memberUid }) {
  const [letter, setLetter] = useState(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const draft = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/family-members/${memberUid}/nvc-letter`);
      setLetter(data);
      setBody(data.body);
    } catch (err) {
      toast.error(friendlyError(err, "Couldn't draft the letter. Try again in a moment."));
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied — paste it into the NVC Public Inquiry Form.`);
    } catch {
      toast.error("Copy failed — select the text and copy manually.");
    }
  };

  if (!letter) {
    return (
      <div className="mb-5">
        <button
          type="button"
          className="w-full btn-ghost text-sm flex items-center justify-center gap-2 py-2.5"
          style={{ border: "1px dashed var(--ai-border)", background: "var(--ai-bg)", color: "var(--text-primary)" }}
          onClick={draft}
          disabled={loading}
          data-testid="draft-nvc-letter-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" style={{ color: "var(--action)" }} />}
          {loading ? "Drafting…" : "Draft my NVC letter — ready to copy-paste"}
        </button>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-lg border p-4" style={{ borderColor: "var(--ai-border)", background: "var(--ai-bg)" }} data-testid="nvc-letter-panel">
      <div className="flex items-center gap-1.5 font-semibold text-sm mb-2" style={{ color: "var(--text-primary)" }}>
        <Mail className="w-4 h-4" style={{ color: "var(--action)" }} /> Your NVC letter — review, fill any [BRACKETS], then copy
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input
          className="field-input text-xs flex-1"
          value={letter.subject}
          readOnly
          data-testid="nvc-letter-subject"
        />
        <button type="button" className="btn-ghost text-xs px-2 py-1.5 flex items-center gap-1"
          style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
          onClick={() => copy(letter.subject, "Subject")} data-testid="nvc-letter-copy-subject-btn">
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <textarea
        className="field-input text-xs font-mono w-full"
        rows={12}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        data-testid="nvc-letter-body"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-primary text-xs py-2 flex items-center gap-1.5"
          onClick={() => copy(body, "Letter")} data-testid="nvc-letter-copy-body-btn">
          <Copy className="w-3.5 h-3.5" /> Copy letter
        </button>
        <a href={letter.inquiry_url} target="_blank" rel="noopener noreferrer"
          className="btn-ghost text-xs py-2 flex items-center gap-1.5"
          style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
          data-testid="nvc-letter-inquiry-link">
          <ExternalLink className="w-3.5 h-3.5" /> Open NVC Public Inquiry Form
        </a>
      </div>
    </div>
  );
}
